import { OfficeMember } from '@/components/OfficeMember';
import { FetchImportExportHistoriesArgs } from '@/context/services/import_export_histories/ImportExportHistories.service';
import { SearchFormData } from '@/features/import_export_histories/type';
import {
  isValidSource,
  isValidType,
} from '@/features/import_export_histories/utils/typeGuards';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Form,
  FormDatepicker,
  FormSelect,
  SelectProps,
  type DatepickerProps,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { toISODateString } from 'date-util';
import { FC, memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styles from '../Search.module.scss';

const cx = classNames.bind(styles);

const defaultFormValues: SearchFormData = {
  type: '',
  source: '',
  office_member_ids: [],
  submitted_date: ['', ''],
};

export type Props = {
  typeOptions?: BaseOption[];
  sourceOptions?: BaseOption[];
  officeMemberDefaultOptions?: BaseOption[];
  onSearch?: (params: FetchImportExportHistoriesArgs) => void;
  isLoading?: boolean;
  initialValues?: Partial<SearchFormData>;
};

export const SearchForm: FC<Props> = memo(
  ({
    typeOptions = [],
    sourceOptions = [],
    officeMemberDefaultOptions = [],
    onSearch,
    isLoading = false,
    initialValues,
  }) => {
    const { t } = useTranslation();

    const form = useForm<SearchFormData>({
      defaultValues: {
        ...defaultFormValues,
        ...initialValues,
      },
      mode: 'all',
    });

    const handleSubmit = useCallback(
      (data: SearchFormData) => {
        const [from, to] = data.submitted_date ?? [];

        const type =
          data.type && isValidType(data.type) ? data.type : undefined;

        const source =
          data.source && isValidSource(data.source) ? data.source : undefined;

        onSearch?.({
          type,
          source,
          office_member_ids: data.office_member_ids?.length
            ? data.office_member_ids
            : undefined,
          submitted_at_from: from ? toISODateString(from) : undefined,
          submitted_at_to: to ? toISODateString(to) : undefined,
          page: 1,
        });
      },
      [onSearch]
    );

    const handleReset = useCallback(() => {
      form.reset(defaultFormValues);
    }, [form]);

    const typeSelectProps = useMemo<SelectProps>(
      () => ({
        options: typeOptions,
        allowClear: true,
      }),
      [typeOptions]
    );

    const sourceSelectProps = useMemo<SelectProps>(
      () => ({
        options: sourceOptions,
        allowClear: true,
      }),
      [sourceOptions]
    );

    const officeMemberSelectProps = useMemo(
      () => ({
        maxTagCount: 'responsive' as const,
      }),
      []
    );

    const datepickerProps = useMemo<DatepickerProps>(
      () => ({
        placeholder: [t('date_gteq_placeholder'), t('date_lteq_placeholder')],
      }),
      [t]
    );

    const labels = useMemo(
      () => ({
        type: { text: t('import_export_history_type') },
        source: { text: t('import_export_history_target_data') },
        executedBy: { text: t('import_export_history_executed_by') },
        submittedAt: { text: t('import_export_history_submitted_at') },
      }),
      [t]
    );

    return (
      <Form<SearchFormData>
        formMethod={form}
        onSubmit={handleSubmit}
        testId='import-export-history-search-form'
      >
        <div className={cx('form-row')}>
          <div className={cx('field')}>
            <FormSelect<SearchFormData>
              control={form.control}
              name='type'
              selectProps={typeSelectProps}
              label={labels.type}
              inputId='import-export-history-type'
              testId='import-export-history-type'
            />
          </div>
          <div className={cx('field')}>
            <FormSelect<SearchFormData>
              control={form.control}
              name='source'
              selectProps={sourceSelectProps}
              label={labels.source}
              inputId='import-export-history-source'
              testId='import-export-history-source'
            />
          </div>
          <div className={cx('field')}>
            <OfficeMember<SearchFormData>
              control={form.control}
              name='office_member_ids'
              label={labels.executedBy}
              inputId='import-export-history-executed-by'
              testId='import-export-history-executed-by'
              defaultOptions={officeMemberDefaultOptions}
              selectProps={officeMemberSelectProps}
            />
          </div>
          <div className={cx('field', 'date-field')}>
            <FormDatepicker<SearchFormData>
              control={form.control}
              name='submitted_date'
              datepickerProps={datepickerProps}
              label={labels.submittedAt}
              inputId='import-export-history-submitted-date'
              testId='import-export-history-submitted-date'
            />
          </div>
          <div className={cx('actions')}>
            <ButtonV2
              type='button'
              isTertiary
              onClick={handleReset}
              testId='import-export-history-search-clear'
              size='sm'
            >
              {t('clear')}
            </ButtonV2>
            <ButtonV2
              type='submit'
              size='sm'
              color='primary'
              isSecondary
              isLoading={isLoading}
              testId='import-export-history-search-submit'
            >
              {t('search')}
            </ButtonV2>
          </div>
        </div>
      </Form>
    );
  }
);
SearchForm.displayName = 'SearchForm';
